// Scaled-iframe thumbnail for sourceFormat='html' and 'mjml' section rows.
// Mirrors SectionCardPreview's contain-style sizing (natural width 600,
// scaled down to displayWidth) but uses a sandboxed iframe with srcDoc since
// we can't render arbitrary HTML through React without losing inline styles.
//
// Fully dynamic, like the node-format cards: `$brand.*` / `$product[N].*`
// tokens resolve against the active brand at view time (no server renders,
// no cached thumbnails to go stale), and mjml rows compile through
// mjml-browser in the client. `$auto.*` / `[NM:*]` tokens stay literal —
// those are send-time constructs.

import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect, useMemo } from 'react';

import Box from '@mui/material/Box';

import { compileMjmlFragmentInBrowser } from '@/lib/email-preview/compile-live-sections';
import { resolveMergeTags, type BrandResolverContext } from './merge-tag-resolver';

interface Props {
  /** Raw body — HTML for sourceFormat='html', MJML for sourceFormat='mjml'. */
  rawHtml: string;
  /** Compile the body through mjml-browser before rendering. */
  isMjml?: boolean;
  /** Active brand for merge-tag resolution; tokens stay literal when absent. */
  brand?: BrandResolverContext;
  displayWidth: number;
  maxDisplayHeight?: number;
  minDisplayHeight?: number;
}

const NATURAL_WIDTH = 600;

export function SectionRawHtmlCardPreview({
  rawHtml,
  isMjml = false,
  brand,
  displayWidth,
  maxDisplayHeight,
  minDisplayHeight = 60,
}: Props) {
  const { t } = useTranslation('emailBuilder');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [naturalHeight, setNaturalHeight] = useState(minDisplayHeight);

  // Brand tokens resolve at view time so the card shows the client's actual
  // colors/logo/products, exactly like node-format previews.
  const resolvedBody = useMemo(() => resolveMergeTags(rawHtml, brand, undefined), [rawHtml, brand]);

  // mjml rows compile client-side (async — mjml-browser v5). Until the
  // compile lands the iframe renders nothing, which reads as a brief blank
  // card rather than raw <mj-*> tag soup.
  const [compiledMjml, setCompiledMjml] = useState<string | null>(null);
  useEffect(() => {
    if (!isMjml) {
      setCompiledMjml(null);
      return;
    }
    let cancelled = false;
    void compileMjmlFragmentInBrowser(resolvedBody).then((html) => {
      if (!cancelled) setCompiledMjml(html ?? '');
    });
    return () => {
      cancelled = true;
    };
  }, [isMjml, resolvedBody]);

  const body = isMjml ? (compiledMjml ?? '') : resolvedBody;

  // Build a self-contained doc with the fragment centered in a 600px
  // column so the cardthumbnail matches what the row will look like
  // inside an email body.
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>
    html,body{margin:0;padding:0;background:#fff;font-family:Helvetica,Arial,sans-serif;color:#111;}
    table{border-collapse:collapse;}
    img{display:block;max-width:100%;height:auto;}
    a{color:inherit;}
  </style></head><body>${body}</body></html>`;

  // Measure body height after the iframe loads so the card is as tall as
  // its content — same UX as the node-format preview's natural-height
  // model. We poll for ~2s post-load to absorb image reflow.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let frame = 0;
    let ticks = 0;
    let last = 0;
    const measure = () => {
      // A sandboxed (`sandbox=""`) iframe is a unique opaque origin, so reading
      // `contentWindow.document` throws a cross-origin SecurityError. Guard it so
      // a failed measure degrades to min height instead of crashing the page.
      let doc: Document | null | undefined;
      try {
        doc = iframe.contentDocument || iframe.contentWindow?.document;
      } catch {
        doc = null;
      }
      if (doc?.body) {
        const h = doc.body.scrollHeight;
        if (h !== last) {
          last = h;
          setNaturalHeight(Math.max(h, minDisplayHeight));
        }
      }
      ticks += 1;
      if (ticks < 120) frame = requestAnimationFrame(measure);
    };
    const onLoad = () => {
      frame = requestAnimationFrame(measure);
    };
    iframe.addEventListener('load', onLoad);
    return () => {
      iframe.removeEventListener('load', onLoad);
      cancelAnimationFrame(frame);
    };
  }, [body, minDisplayHeight]);

  // Scale-to-fit: pick the smaller of width-fit and height-fit so the
  // entire fragment is visible in the card. Raw HTML is opaque (you can't
  // recognize a footer from its top crop the way you can a node section),
  // so we'd rather show a tiny full preview than a clipped large one.
  const widthScale = displayWidth / NATURAL_WIDTH;
  const heightScale = maxDisplayHeight && naturalHeight > 0
    ? maxDisplayHeight / naturalHeight
    : widthScale;
  const scale = Math.min(widthScale, heightScale);
  const scaledHeight = Math.max(minDisplayHeight, naturalHeight * scale);

  return (
    <Box
      sx={{
        width: displayWidth,
        height: scaledHeight,
        overflow: 'hidden',
        bgcolor: '#fff',
        position: 'relative',
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <iframe
        ref={iframeRef}
        title={t('sectionRawHtmlCardPreview.iframeTitle', 'Section preview')}
        srcDoc={srcDoc}
        sandbox=""
        style={{
          width: NATURAL_WIDTH,
          height: naturalHeight,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          display: 'block',
          flexShrink: 0,
        }}
      />
    </Box>
  );
}
